import React, { useEffect, useState } from "react";
import CustomButton from "../../UI/Buttons/CustomButton";
import LeftArrow from "../../UI/Icons/LeftArrow";
import {
  ICandidateDetails,
  TDeclinedWithdrawnJobs,
} from "../../../store/superadmin/types";
import VacancyPosition from "../../UI/VacancyPosition";
import { formatTimeframes } from "../../Vacancy/timeframeApiHelper";

interface CandidateJobsProps {
  reviewCandidateData: ICandidateDetails | null;
  setShowCandidateJobs: (show: boolean) => void;
  setShowAdminComments: (show: boolean) => void;
  typeofCandidateJobs: string; // "declined" or "withdrawn"
}

/**
 * AdminComments component allows administrators to view, add, and delete comments
 * related to a specific candidate. It fetches the comments from the server, displays
 * them in a list, and provides a form for adding new comments.
 *
 * @component
 * @param {CandidateJobsProps} props - The props for the AdminComments component.
 * @param {number} props.candidateId - The ID of the candidate for whom the comments are being managed.
 * @param {(date: string) => string} props.formatDateToReadable - A function to format dates into a readable string.
 * @param {(show: boolean) => void} props.setShowAdminComments - A function to toggle the visibility of the AdminComments component.
 *
 * @returns {JSX.Element} The rendered AdminComments component.
 *
 * @remarks
 * - This component uses Redux for state management and dispatches actions to fetch, add, and delete comments.
 * - It uses Formik for form handling and validation when adding new comments.
 * - The component includes a preloader for asynchronous actions and handles errors gracefully.
 *
 */
const CandidateJobs: React.FC<CandidateJobsProps> = ({
  reviewCandidateData,
  setShowCandidateJobs,
  setShowAdminComments,
  typeofCandidateJobs,
}) => {
  const [currentJobsList, setCurrentJobsList] = useState<
    Array<TDeclinedWithdrawnJobs>
  >([]);

  useEffect(() => {
    if (reviewCandidateData) {
      const jobsList =
        typeofCandidateJobs === "declined"
          ? reviewCandidateData.declined_jobs
          : reviewCandidateData.withdraw_jobs;
      setCurrentJobsList(jobsList);
    }
  }, [typeofCandidateJobs, setCurrentJobsList, reviewCandidateData]);

  const formatCustomDate = (dateString: string | null) => {
    const date = new Date(dateString as string);

    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    const dayName = days[date.getUTCDay()];
    const day = date.getUTCDate();
    const monthName = months[date.getUTCMonth()];
    const year = date.getUTCFullYear();

    let hours = date.getUTCHours();
    let minutes = date.getUTCMinutes();
    const ampm = hours >= 12 ? 'pm' : 'am';

    hours = hours % 12;
    hours = hours ? hours : 12; // 0 becomes 12
    const minutesStr = minutes < 10 ? '0' + minutes : minutes;

    return `${dayName} ${day} ${monthName} ${year}, ${hours}:${minutesStr}${ampm}`;
  }

  return (
    <>
      <div className="w-100 h-1px bg-grey-light "></div>
      <div className="d-flex flex-column gap-10">
        <div className="d-flex flex-column flex-lg-row gap-20 align-items-lg-center justify-content-lg-between">
          <CustomButton
            variant={"primary"}
            type="button"
            className="btn-theme bg-off-white text-black"
            onClick={() => setShowCandidateJobs(false)}
          >
            <LeftArrow />
            Back to Profile
          </CustomButton>
          <div className="fs-22 fw-400 text-black">
            Jobs {typeofCandidateJobs === "declined" ? "Declined" : "Withdrawn"}
          </div>
        </div>
        <div className="d-flex flex-column gap-10">
          {currentJobsList && currentJobsList.length > 0 ? (
            currentJobsList.map((job, index) => (
              <div
                key={index}
                className="border border-off-white radius-8px bg-white p-20px d-flex flex-column gap-10 text-break"
              >
                <div className="d-flex flex-column flex-lg-row gap-40 gap-mobile-10">
                  <div className="d-flex flex-column gap-3 fs-13 fw-400 min-w-170px w-170px mobile-w-100">
                    <div className="text-grey-medium">
                      Date and Time {typeofCandidateJobs === "declined" ? "Declined" : "Withdrawn"}
                    </div>
                    <div className="text-black">{typeofCandidateJobs === "declined" ? formatCustomDate(job.declined_at) : formatCustomDate(job.withdrawn_at)}</div>
                  </div>
                  <div className="d-flex flex-column gap-3 fs-13 fw-400 min-w-210px w-210px mobile-w-100">
                    <div className="text-grey-medium">Requested Timeframe</div>
                    <div className="text-black">
                      {job?.vacancy?.timeframes &&
                        formatTimeframes(job?.vacancy?.timeframes)?.map(
                          (formatedTimeData, index) => (
                            <div
                              key={index}
                              className={`fs-13 ${
                                formatedTimeData.alert
                                  ? "text-alert fw-700"
                                  : "fw-400"
                              }`}
                            >
                              {formatedTimeData.label}
                            </div>
                          )
                        )}
                    </div>
                  </div>
                  <div className="d-flex flex-column gap-3 fs-13 fw-400 min-w-210px w-210px mobile-w-100">
                    <div className="text-grey-medium">Position</div>
                    <div className="text-black">
                      <VacancyPosition
                        positions={job.vacancy?.vacancyPositions ?? []}
                        alternatePosition={
                          job.vacancy?.alternate_position_legacy ?? ""
                        }
                      />
                    </div>
                  </div>
                  <div className="d-flex flex-column gap-3 fs-13 fw-400 min-w-210px w-210px mobile-w-100">
                    <div className="text-grey-medium">Centre</div>
                    <div className="text-black">
                      {job.vacancy?.centre?.business_name}
                    </div>
                  </div>
                </div>
                {typeofCandidateJobs === "declined" ? 
                  <div className="d-flex flex-column gap-3 fs-13 fw-400">
                    <div className="text-grey-medium">
                      Reason Provided by the Employee:
                    </div>
                    <div className="fs-16 text-black">{job.status_comment}</div>
                  </div>
                : "" }
              </div>
            ))
          ) : (
            <span>No jobs found</span>
          )}
        </div>
      </div>
    </>
  );
};

export default CandidateJobs;
